import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import QualificationFormModal from '@/components/QualificationForm';
import { Mail, MapPin, CheckCircle2, Send } from 'lucide-react';

import { BACKEND_URL } from '@/config/api';
import { publicCatalog } from '@/config/membershipCatalog';
import { useFormToken, honeypotStyle } from '@/hooks/useFormToken';
import { useSiteLang } from '@/components/site/SiteLang';

const API = BACKEND_URL;

const T = {
  es: {
    badge: 'Contacto',
    title: 'Cada gran decisión empieza con una',
    titleAccent: 'conversación.',
    desc: 'Si estás aquí es porque algo ha resonado. Cuéntanos qué necesitas y te respondemos con la misma claridad con la que operamos.',
    infoTitle: 'Información directa.',
    infoDesc: 'No tenemos un call center. Tenemos un equipo pequeño que lee cada mensaje y te responde personalmente.',
    emailLabel: 'Email',
    officeLabel: 'Oficina',
    country: 'Estados Unidos',
    replyLabel: 'Cuándo te respondemos:',
    replyText: 'si aún no eres miembro, lo antes posible. Si ya lo eres, según tu membresía: Access, en un máximo de 48 h laborables; PRO y Premium, en un máximo de 24 h laborables. Si tu consulta es sobre membresías o el bootcamp, incluye tu perfil profesional: nos ayuda a darte una respuesta más precisa.',
    joinTitle: '¿Quieres formar parte del club?',
    joinDesc: 'El camino más rápido es el test de idoneidad: menos de 2 minutos y, al terminar, eliges día y hora para hablar con el equipo.',
    formTitle: 'Escríbenos',
    formNote: 'Todos los campos son obligatorios.',
    successTitle: 'Mensaje recibido.',
    successDesc: 'Nuestro equipo lo revisará y te responderá lo antes posible.',
    sendAnother: 'Enviar otro mensaje',
    nameLabel: 'Nombre completo',
    namePlaceholder: 'Tu nombre',
    emailPlaceholder: 'tu@email.com',
    messageLabel: 'Mensaje',
    messagePlaceholder: 'Cuéntanos en qué podemos ayudarte — membresías, formación, bootcamp, colaboraciones...',
    error: 'Hubo un error al enviar. Inténtalo de nuevo o escríbenos directamente a info@freedomhomes.club',
    sending: 'Enviando...',
    send: 'Enviar mensaje',
    privacyPre: 'Al enviar aceptas la',
    privacyLink: 'política de privacidad',
  },
  en: {
    badge: 'Contact',
    title: 'Every big decision starts with a',
    titleAccent: 'conversation.',
    desc: 'If you’re here, something has struck a chord. Tell us what you need and we’ll reply with the same clarity we bring to everything we do.',
    infoTitle: 'Straight to the team.',
    infoDesc: 'We don’t have a call centre. We have a small team that reads every message and replies to you personally.',
    emailLabel: 'Email',
    officeLabel: 'Office',
    country: 'United States',
    replyLabel: 'When we reply:',
    replyText: 'if you’re not a member yet, as soon as we can. If you are, it depends on your membership: Access, within 48 working hours; PRO and Premium, within 24 working hours. If your query is about memberships or the bootcamp, tell us a little about your professional background: it helps us give you a more precise answer.',
    joinTitle: 'Want to join the club?',
    joinDesc: 'The quickest route is the suitability test: it takes under 2 minutes and, once you finish, you choose a day and time to speak with the team.',
    formTitle: 'Write to us',
    formNote: 'All fields are required.',
    successTitle: 'Message received.',
    successDesc: 'Our team will read it and get back to you as soon as possible.',
    sendAnother: 'Send another message',
    nameLabel: 'Full name',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'you@email.com',
    messageLabel: 'Message',
    messagePlaceholder: 'Tell us how we can help: memberships, training, the bootcamp, partnerships…',
    error: 'Something went wrong sending your message. Please try again or email us directly at info@freedomhomes.club',
    sending: 'Sending…',
    send: 'Send message',
    privacyPre: 'By sending, you accept the',
    privacyLink: 'privacy policy',
  },
};

export default function Contacto() {
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const formToken = useFormToken();  // defensa frente a bots (backend/antibot.py)
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const cat = publicCatalog(lang);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      const token = await formToken();
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          whatsapp: 'No proporcionado',
          source: 'contacto',
          message: form.message,
          form_token: token || undefined,
          website: form.website || undefined,
        }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', message: '', website: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafbfc]">

      {/* Hero */}
      <section className="pt-32 sm:pt-40 pb-16 sm:pb-20 bg-[#0a1628] relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 border border-[#c9a227]/30 bg-[#c9a227]/10 px-4 py-2 mb-8">
            <Mail className="w-4 h-4 text-[#c9a227]" />
            <span className="font-body text-xs uppercase tracking-widest text-[#c9a227]">{t.badge}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white leading-tight mb-6" data-testid="contact-headline">
            {t.title} <span className="italic text-[#c9a227]">{t.titleAccent}</span>
          </h1>
          <p className="font-body text-lg text-white/60 max-w-2xl mx-auto">
            {t.desc}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 md:py-28" data-testid="contact-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12">
            {/* Info Column */}
            <div className="md:col-span-2">
              <h2 className="font-heading text-2xl text-[#0a1628] mb-6">{t.infoTitle}</h2>
              <p className="font-body text-slate-600 mb-10 leading-relaxed">
                {t.infoDesc}
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0a1628] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#c9a227]" />
                  </div>
                  <div>
                    <p className="font-body text-xs uppercase tracking-widest text-[#c9a227] mb-1">{t.emailLabel}</p>
                    <a href="mailto:info@freedomhomes.club" className="font-body text-[#0a1628] hover:text-[#c9a227] transition-colors" data-testid="contact-email">
                      info@freedomhomes.club
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0a1628] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#c9a227]" />
                  </div>
                  <div>
                    <p className="font-body text-xs uppercase tracking-widest text-[#c9a227] mb-1">{t.officeLabel}</p>
                    <p className="font-body text-[#0a1628]" data-testid="contact-address">
                      407 Lincoln Rd Ste 12-N<br />
                      Miami Beach, FL 33139<br />
                      {t.country}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-[#0a1628]">
                <p className="font-body text-sm text-white/70 leading-relaxed">
                  <span className="text-[#c9a227] font-semibold">{t.replyLabel}</span> {t.replyText}
                </p>
              </div>

              <div className="mt-6 p-6 border border-[#c9a227]/40 bg-[#c9a227]/[0.06]" data-testid="contact-test-box">
                <p className="font-heading text-lg text-[#0a1628] mb-2">{t.joinTitle}</p>
                <p className="font-body text-sm text-slate-600 leading-relaxed mb-4">
                  {t.joinDesc}
                </p>
                <Button type="button" onClick={() => setIsModalOpen(true)} data-testid="contact-test-cta"
                  className="w-full sm:w-auto h-12 px-6 bg-[#c9a227] text-[#0a1628] hover:bg-[#d4ad2e] font-semibold rounded-none">
                  {cat.testCta}
                </Button>
              </div>
            </div>

            {/* Form Column */}
            <div className="md:col-span-3">
              <div className="bg-white p-5 sm:p-8 md:p-10 border border-slate-200 shadow-sm" data-testid="contact-form-wrapper">
                <h2 className="font-heading text-2xl text-[#0a1628] mb-2">{t.formTitle}</h2>
                <p className="font-body text-sm text-slate-500 mb-8">{t.formNote}</p>

                {status === 'success' ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 mx-auto bg-[#c9a227]/10 flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-8 h-8 text-[#c9a227]" />
                    </div>
                    <h3 className="font-heading text-2xl text-[#0a1628] mb-3">{t.successTitle}</h3>
                    <p className="font-body text-slate-600 mb-6">
                      {t.successDesc}
                    </p>
                    <Button
                      onClick={() => setStatus(null)}
                      className="h-12 px-6 bg-[#0a1628] text-white hover:bg-[#162a4a] rounded-none"
                    >
                      {t.sendAnother}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6 relative" data-testid="contact-form">
                    <div aria-hidden="true" style={honeypotStyle}>
                      <label>Web <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></label>
                    </div>
                    <div>
                      <label className="font-body text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t.nameLabel}</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full h-12 px-4 border border-slate-200 bg-[#fafbfc] font-body text-[#0a1628] focus:border-[#c9a227] focus:outline-none transition-colors"
                        placeholder={t.namePlaceholder}
                        required
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t.emailLabel}</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full h-12 px-4 border border-slate-200 bg-[#fafbfc] font-body text-[#0a1628] focus:border-[#c9a227] focus:outline-none transition-colors"
                        placeholder={t.emailPlaceholder}
                        required
                        data-testid="contact-input-email"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t.messageLabel}</label>
                      <textarea
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={5}
                        className="w-full px-4 py-3 border border-slate-200 bg-[#fafbfc] font-body text-[#0a1628] focus:border-[#c9a227] focus:outline-none transition-colors resize-none"
                        placeholder={t.messagePlaceholder}
                        required
                        data-testid="contact-input-message"
                      />
                    </div>

                    {status === 'error' && (
                      <p className="font-body text-sm text-red-500" data-testid="contact-error">
                        {t.error}
                      </p>
                    )}

                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full h-14 bg-[#c9a227] text-[#0a1628] hover:bg-[#d4ad2e] font-semibold tracking-wide rounded-none inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="contact-submit"
                    >
                      {sending ? t.sending : t.send}
                      {!sending && <Send className="w-4 h-4" />}
                    </Button>
                    <p className="font-body text-xs text-slate-500 text-center" data-testid="contact-privacy-note">
                      {t.privacyPre}{' '}
                      <a href="/privacy-policy-fhc" className="underline hover:text-[#c9a227]">{t.privacyLink}</a>.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <QualificationFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} source="contacto" lang={lang} />
    </div>
  );
}
